/**
 * Espaço de trabalho de um contexto fiscal: paleta, canvas, roteiros e saídas.
 * As abas ECE, Distribuidora e Produtora são o mesmo montador com
 * contexto e paleta de papéis diferentes.
 */

import { Loader2, Sparkles } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { BotaoMicrofone } from "@/components/canvas/BotaoMicrofone";
import {
  AcoesCanvas,
  BarraFerramentas,
  FlowCanvas,
  LegendaSetas,
} from "@/components/canvas/FlowCanvas";
import { Entregaveis } from "@/components/fiscal/Entregaveis";
import { PnlCard } from "@/components/fiscal/PnlCard";
import { ResumoOperacao } from "@/components/fiscal/ResumoOperacao";
import { type CorrigirContabil, RoteiroContabil } from "@/components/fiscal/RoteiroContabil";
import type { Corrigir, EstadoGravacao } from "@/components/fiscal/CorrecaoFiscal";
import { RoteiroFiscal } from "@/components/fiscal/RoteiroFiscal";
import { CardsOrdenaveis } from "@/components/layout/CardsOrdenaveis";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAssistenteStatus,
  useCalculo,
  useModelos,
  useMontarPorTexto,
  useTerminais,
  useUfs,
} from "@/hooks/queries";
import { useOperacao, type EstadoOperacao } from "@/hooks/useOperacao";
import { useAuth } from "@/lib/auth";
import type {
  Aresta,
  Contexto,
  No,
  Produto,
  SubstituicoesContabil,
  SubstituicoesNfs,
  TipoNo,
} from "@/types";

interface Props {
  contexto: Contexto;
  titulo: string;
  descricao: string;
  /** Papéis oferecidos na paleta deste contexto. */
  papeis: TipoNo[];
  /** Produtos selecionáveis (a Distribuidora inclui diesel). */
  produtos: Produto[];
  /** Fluxo-modelo inicial; também separa o estado salvo de cada montador. */
  chaveModelo: string;
  /** Sub-aba dentro do contexto (vai para a categoria em "Minhas operações"). */
  variante?: string;
  /** Fluxo já gravado (ex.: envio para aprovação) — abre no lugar do fluxo-modelo. */
  salvo?: EstadoOperacao;
  /** Só visualização: sem paleta, edição, montagem por descrição nem entregáveis. */
  somenteLeitura?: boolean;
  /** Ids dos cards exibidos (ex.: o time Tax vê só fluxo, resumo e roteiro fiscal); todos se omitido. */
  cardsVisiveis?: string[];
  /**
   * Libera as setas do roteiro fiscal mesmo em `somenteLeitura` (o time Tax
   * ajustando um envio) e recebe cada ordem nova para gravar.
   */
  onReordenarNfs?: (ordem: string[]) => void;
  /** Correções do Tax ao roteiro fiscal de um envio. */
  substituicoesNfs?: SubstituicoesNfs;
  /** Libera a correção dos valores do roteiro fiscal (time Tax) e recebe cada uma. */
  onCorrigirValor?: Corrigir;
  /** Estado da gravação das correções, para o cabeçalho do roteiro fiscal. */
  gravacaoCorrecoes?: EstadoGravacao;
  onTentarGravarDeNovo?: () => void;
  /** Correções do Contábil ao roteiro contábil de um envio. */
  substituicoesContabil?: SubstituicoesContabil;
  /** Libera a correção das colunas do roteiro contábil (time Contábil) e recebe cada uma. */
  onCorrigirContabil?: CorrigirContabil;
  /** Estado da gravação das correções do Contábil, para o cabeçalho do roteiro contábil. */
  gravacaoContabil?: EstadoGravacao;
  onTentarGravarContabilDeNovo?: () => void;
}

export function MontadorOperacao({
  contexto,
  titulo,
  descricao,
  papeis,
  produtos,
  chaveModelo,
  variante,
  salvo,
  somenteLeitura = false,
  cardsVisiveis,
  onReordenarNfs,
  substituicoesNfs,
  onCorrigirValor,
  gravacaoCorrecoes,
  onTentarGravarDeNovo,
  substituicoesContabil,
  onCorrigirContabil,
  gravacaoContabil,
  onTentarGravarContabilDeNovo,
}: Props) {
  const { podeEditar: podeEditarConta } = useAuth();
  const podeEditar = podeEditarConta && !somenteLeitura;
  const { data: ufs } = useUfs();
  const { data: terminais } = useTerminais();
  const { data: modelos } = useModelos();

  const modelo = modelos?.templates[chaveModelo];

  const controle = useOperacao(
    salvo ?? {
      nome: titulo,
      nodes: [],
      edges: [],
      produto: produtos[0] ?? "hidratado",
      anidroDiferido: true,
      precoCompra: "3400",
      precoVenda: "3620",
      volume: "1000",
    },
    contexto,
    chaveModelo,
  );
  const { estado, restaurado, pedido, alterar, substituirGrafo, atualizarNo } = controle;

  /*
    Carrega o fluxo-modelo assim que a referência chega (canvas nunca abre vazio)
    — mas só na primeira visita da sessão. Voltando de outro menu, vale o que o
    usuário deixou, mesmo que seja um canvas limpo.
  */
  const carregado = React.useRef(restaurado || Boolean(salvo));
  React.useEffect(() => {
    if (carregado.current || !modelo) return;
    carregado.current = true;
    substituirGrafo(modelo.nodes as No[], modelo.edges as Aresta[]);
  }, [modelo, substituirGrafo]);

  /*
    A matriz origem × destino manda o par pela query string (?origem&destino) —
    é o atalho "clique na célula" da aplicação original. Aplica-se uma vez, já
    com o fluxo-modelo carregado, e a query é limpa para não ressemear a cada
    troca de aba ou recarga.
  */
  const [parametros, setParametros] = useSearchParams();
  const semeado = React.useRef(false);
  React.useEffect(() => {
    if (contexto !== "ordem" || semeado.current || !estado.nodes.length) return;
    const origem = parametros.get("origem");
    const destino = parametros.get("destino");
    if (!origem && !destino) return;
    semeado.current = true;

    const produtor = estado.nodes.find((n) => n.tipo === "PRODUTOR");
    const cliente = estado.nodes.find((n) => n.tipo === "DISTRIBUIDORA");
    if (origem && produtor) atualizarNo(produtor.id, { uf: origem });
    if (destino && cliente) atualizarNo(cliente.id, { uf: destino });

    const limpos = new URLSearchParams(parametros);
    limpos.delete("origem");
    limpos.delete("destino");
    setParametros(limpos, { replace: true });
  }, [contexto, estado.nodes, parametros, setParametros, atualizarNo]);

  const { data: resultado, isFetching, error } = useCalculo(pedido);
  /*
    Os balões ficam fora do pedido de cálculo (arrastar um não é motivo para
    recalcular) e entram só no que sai daqui: PDFs, fluxograma e envio.
  */
  const pedidoEntregaveis = React.useMemo(
    () => pedido && { ...pedido, baloes: estado.baloes ?? {} },
    [pedido, estado.baloes],
  );
  const montarPorTexto = useMontarPorTexto();
  const { data: assistente } = useAssistenteStatus();
  const [texto, setTexto] = React.useState("");

  const carregarModelo = () => {
    if (!modelo) return;
    substituirGrafo(modelo.nodes as No[], modelo.edges as Aresta[]);
    toast.success("Fluxo-modelo carregado.");
  };

  const montar = () => {
    if (!texto.trim()) return;
    montarPorTexto.mutate(
      { descricao: texto, contexto },
      {
        onSuccess: (plano) => {
          substituirGrafo(plano.nodes as No[], plano.edges as Aresta[]);
          if (plano.produto && produtos.includes(plano.produto)) {
            alterar("produto", plano.produto);
          } else {
            const txt = texto.toLowerCase();
            if ((txt.includes("hidratado") || txt.includes("ehc")) && produtos.includes("hidratado")) {
              alterar("produto", "hidratado");
            } else if ((txt.includes("anidro") || txt.includes("eac")) && produtos.includes("anidro")) {
              alterar("produto", "anidro");
            }
          }
          toast.success("Fluxo montado a partir da descrição.");
        },
        onError: (erro: Error) => toast.error(erro.message),
      },
    );
  };

  if (!ufs || !terminais || !modelos) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const rotuloProduto = ufs.produtos[estado.produto] ?? estado.produto;

  const cardConfiguracao = (
    <Card className="nao-imprimir">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{titulo}</CardTitle>
            <CardDescription>{descricao}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <div>
          <Label htmlFor="nome-fluxo">Nome do fluxo</Label>
          {/*
            O `Input` nasce com a fonte monoespaçada dos campos numéricos; o
            nome do fluxo é texto corrido e ficava destoando dos selects ao
            lado, daí a volta para a fonte da interface.
          */}
          <Input
            id="nome-fluxo"
            value={estado.nome}
            disabled={somenteLeitura}
            onChange={(e) => alterar("nome", e.target.value)}
            className="!font-sans !tracking-normal font-bold ![font-variant-numeric:normal] ![font-feature-settings:normal]"
          />
        </div>
        <div>
          <Label>Produto</Label>
          <Select
            value={estado.produto}
            disabled={somenteLeitura}
            onValueChange={(valor) => alterar("produto", valor as Produto)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {produtos.map((produto) => (
                <SelectItem key={produto} value={produto}>
                  {ufs.produtos[produto]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {estado.produto === "anidro" && (
          <div>
            <Label>Destinação da venda (anidro)</Label>
            <Select
              value={estado.anidroDiferido ? "diferido" : "tributado"}
              disabled={somenteLeitura}
              onValueChange={(valor) => alterar("anidroDiferido", valor === "diferido")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="diferido">Distribuidora (diferido)</SelectItem>
                <SelectItem value="tributado">Usina / outros fins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const cardDescricao = (
    <Card className="nao-imprimir">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Montagem por descrição</CardTitle>
          {assistente?.habilitado && (
            <Badge variant="neutro" title="Modelo configurado no servidor">
              {assistente.modelo}
            </Badge>
          )}
        </div>
        <CardDescription>
          {assistente && !assistente.habilitado
            ? "Nenhum provedor de LLM configurado no servidor — monte o fluxo pela paleta abaixo."
            : "Descreva a operação em português — o assistente monta os nós e as ligações. A montagem manual continua disponível."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row">
        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Ex.: compro de usina no MT pela ECE de SP e revendo para distribuidora em GO, com armazenagem no terminal Logum de Paulínia."
          className="flex-1"
          disabled={assistente?.habilitado === false}
        />
        <div className="flex flex-row items-end gap-2 sm:flex-col sm:items-stretch sm:self-end">
          <BotaoMicrofone
            disabled={!podeEditar || assistente?.habilitado === false}
            onTexto={(ditado) =>
              setTexto((atual) => (atual.trim() ? `${atual.trimEnd()} ${ditado}` : ditado))
            }
          />
          <Button
            onClick={montar}
            disabled={
              !texto.trim() ||
              montarPorTexto.isPending ||
              !podeEditar ||
              assistente?.habilitado === false
            }
          >
            {montarPorTexto.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />}
            Montar fluxo
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const cardFluxo = (
    /*
      O cartão do diagrama lê de cima para baixo em quatro faixas de mesma
      margem lateral: título, ferramentas, desenho e rodapé (legenda + ajuda).
      `overflow-hidden` deixa as faixas irem de borda a borda sem estourar o
      raio do cartão — antes o canvas tinha moldura própria e sobrava um anel
      de cartão-dentro-de-cartão.
    */
    <Card className="overflow-hidden">
      <CardHeader className="nao-imprimir pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Fluxo da operação</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="neutro">{estado.nodes.length} nós</Badge>
            <Badge variant="outline">{rotuloProduto}</Badge>
          </div>
        </div>
      </CardHeader>

      {!somenteLeitura && (
        <BarraFerramentas controle={controle} tipos={papeis} tipoVisual={modelos.tipoVisual} />
      )}

      <CardContent className="p-0">
        <FlowCanvas
          controle={controle}
          resultado={resultado}
          ufs={ufs.ufs}
          nomesUfs={ufs.nomes}
          terminais={terminais}
          tipoVisual={modelos.tipoVisual}
          somenteLeitura={!podeEditar}
        />
        <div className="nao-imprimir space-y-2.5 border-t border-border/70 bg-secondary/40 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
            {!somenteLeitura && (
              <AcoesCanvas controle={controle} onCarregarModelo={carregarModelo} />
            )}
            <div className="text-xs leading-relaxed text-muted-foreground">
              <LegendaSetas />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

  );

  return (
    <div className="space-y-5">
      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm font-semibold text-destructive">
            {error.message}
          </CardContent>
        </Card>
      )}

      <CardsOrdenaveis
        chave={chaveModelo}
        fixo={somenteLeitura}
        itens={[
          { id: "configuracao", rotulo: titulo, conteudo: cardConfiguracao },
          {
            id: "descricao",
            rotulo: "Montagem por descrição",
            conteudo: !somenteLeitura && cardDescricao,
          },
          { id: "fluxo", rotulo: "Fluxo da operação", conteudo: cardFluxo },
          {
            id: "resumo",
            rotulo: "Resumo da operação",
            conteudo: resultado && <ResumoOperacao resumo={resultado.resumo} />,
          },
          {
            id: "roteiro-fiscal",
            rotulo: "Roteiro fiscal",
            conteudo: resultado && (
              <RoteiroFiscal
                nfs={resultado.nfs}
                tipoVisual={modelos.tipoVisual}
                onMover={
                  podeEditar || onReordenarNfs
                    ? (chave, direcao) => {
                        const ordem = controle.moverNf(
                          resultado.nfs.map((nf) => nf.chave),
                          chave,
                          direcao,
                        );
                        if (ordem) onReordenarNfs?.(ordem);
                      }
                    : undefined
                }
                substituicoes={substituicoesNfs}
                onCorrigir={onCorrigirValor}
                gravacao={gravacaoCorrecoes}
                onTentarDeNovo={onTentarGravarDeNovo}
              />
            ),
          },
          {
            id: "roteiro-contabil",
            rotulo: "Roteiro contábil",
            conteudo: resultado && (
              <RoteiroContabil
                blocos={resultado.contabil}
                substituicoes={substituicoesContabil}
                onCorrigir={onCorrigirContabil}
                gravacao={gravacaoContabil}
                onTentarDeNovo={onTentarGravarContabilDeNovo}
              />
            ),
          },
          {
            id: "pnl",
            rotulo: "P&L",
            conteudo: resultado && contexto !== "produtora" && (
              <PnlCard
                pnl={resultado.pnl}
                precoCompra={estado.precoCompra}
                precoVenda={estado.precoVenda}
                volume={estado.volume}
                onAlterar={alterar}
                somenteLeitura={somenteLeitura}
              />
            ),
          },
          {
            id: "entregaveis",
            rotulo: "Entregáveis",
            conteudo: !somenteLeitura && <Entregaveis pedido={pedidoEntregaveis} variante={variante} />,
          },
        ].filter((card) => !cardsVisiveis || cardsVisiveis.includes(card.id))}
      />
    </div>
  );
}
